import type { ReactNode } from "react";
import { AlertTriangle, CircleAlert, CircleCheck, CircleDashed, Clock, Info, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type Tone = "ok" | "info" | "warn" | "danger" | "neutral" | "brand";

export const toneClasses: Record<Tone, string> = {
  ok: "bg-ok-soft text-ok-ink",
  info: "bg-info-soft text-info-ink",
  warn: "bg-warn-soft text-warn-ink",
  danger: "bg-danger-soft text-danger-ink",
  neutral: "bg-surface-2 text-ink-2",
  brand: "bg-brand-soft text-brand-ink",
};

export const toneDot: Record<Tone, string> = {
  ok: "bg-ok",
  info: "bg-info",
  warn: "bg-warn",
  danger: "bg-danger",
  neutral: "bg-ink-3",
  brand: "bg-brand",
};

export const toneIcon: Record<Tone, LucideIcon> = {
  ok: CircleCheck,
  info: Info,
  warn: AlertTriangle,
  danger: CircleAlert,
  neutral: CircleDashed,
  brand: Clock,
};

type BadgeProps = {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  size?: "sm" | "md";
  /** Show a tone-coloured dot (optionally pulsing = live). */
  dot?: boolean | "live";
  icon?: LucideIcon | null;
};

/** Status label: never colour alone — always text, usually icon or dot too. */
export function Badge({ tone = "neutral", children, className, size = "md", dot, icon }: BadgeProps) {
  const Icon = icon === undefined ? null : icon;
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold leading-none",
        size === "sm" ? "h-6 px-2 text-[11px]" : "h-7 px-2.5 text-xs",
        toneClasses[tone],
        className,
      )}
    >
      {dot ? (
        <span
          aria-hidden
          className={cn("h-1.5 w-1.5 shrink-0 rounded-full", toneDot[tone], dot === "live" && "animate-live")}
        />
      ) : null}
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Status badge with the tone's default icon. */
export function StatusBadge({ tone = "neutral", icon, ...rest }: BadgeProps) {
  return <Badge tone={tone} icon={icon === undefined ? toneIcon[tone] : icon} {...rest} />;
}
